import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Route, 
  Plus, 
  MapPin,
  GripVertical,
  Trash2,
  Edit,
  Play,
  Eye,
  CheckCircle2,
  Clock,
  Navigation,
  Loader2,
  Settings,
  X,
  Search,
  AlertCircle,
  Crosshair
} from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createMarkerIcon = (color, label) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      color: white;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

// Sortable stop item component
function SortableStop({ stop, index, onRemove, onEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: stop.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 bg-[#27272a] rounded-md border border-[#3f3f46] mb-2"
      data-testid={`stop-item-${index}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab text-zinc-500 hover:text-zinc-300">
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-500/20 text-blue-400 text-xs px-1.5 py-0">
            {index + 1}
          </Badge>
          <span className="text-sm text-zinc-200 truncate">{stop.label}</span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5 truncate">
          {stop.type === 'site' ? 'Site' : 'Location'} • {stop.geofence_m}m radius
          {stop.full_address && (
            <span className="block truncate text-zinc-600">{stop.full_address}</span>
          )}
        </div>
      </div>
      <button onClick={() => onEdit(stop)} className="text-zinc-500 hover:text-zinc-300">
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={() => onRemove(stop.id)} className="text-red-500/70 hover:text-red-400">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// Map click handler component
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Map bounds adjuster
function MapBoundsAdjuster({ stops }) {
  const map = useMap();
  
  useEffect(() => {
    // P0 FIX: Defensive null check for stops
    if (!stops || !Array.isArray(stops)) {
      return;
    }
    // P0 FIX: Only include stops with valid finite coordinates
    const stopsWithCoords = stops.filter(s => 
      s && 
      Number.isFinite(parseFloat(s.lat)) && 
      Number.isFinite(parseFloat(s.lng))
    );
    if (stopsWithCoords.length > 0) {
      try {
        const bounds = L.latLngBounds(stopsWithCoords.map(s => [parseFloat(s.lat), parseFloat(s.lng)]));
        map.fitBounds(bounds, { padding: [50, 50] });
      } catch (error) {
        console.error('[MapBoundsAdjuster] Error fitting bounds:', error);
      }
    }
  }, [stops, map]);
  
  return null;
}

export default function RoutesPage() {
  const { api, isAdmin } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [viewingRoute, setViewingRoute] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [routeName, setRouteName] = useState('');
  const [routeActive, setRouteActive] = useState(true);
  const [stops, setStops] = useState([]);
  const [editingStop, setEditingStop] = useState(null);
  const [showStopDialog, setShowStopDialog] = useState(false);
  
  // Stop form
  const [stopType, setStopType] = useState('location');
  const [stopSiteId, setStopSiteId] = useState('');
  const [stopLabel, setStopLabel] = useState('');
  const [stopAddress, setStopAddress] = useState('');
  const [stopLat, setStopLat] = useState('');
  const [stopLng, setStopLng] = useState('');
  const [stopGeofence, setStopGeofence] = useState(75);
  const [stopRequired, setStopRequired] = useState(false); // Default OFF - only applies during patrol execution
  const [stopFormError, setStopFormError] = useState('');
  const [addressEntryMode, setAddressEntryMode] = useState('autocomplete'); // 'autocomplete' | 'manual' | 'map_pin' | 'gps'
  
  // Patrols v2: Sector selection state
  const [sectorLocations, setSectorLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationSectors, setLocationSectors] = useState([]);
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingSectors, setLoadingSectors] = useState(false);
  
  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressValidated, setAddressValidated] = useState(false);
  const addressDebounceRef = useRef(null);
  const addressInputRef = useRef(null);
  
  // Default map center (Sydney)
  const defaultCenter = [-33.8688, 151.2093];
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      
      const [routesRes, sitesRes] = await Promise.all([
        api.get('/routes?active_only=false'),
        api.get('/sites?is_active=true')
      ]);
      
      // Defensive: Handle various API response shapes
      let routesData = routesRes.data;
      let sitesData = sitesRes.data;
      
      // If response is wrapped: { routes: [...] } or { data: [...] }
      if (routesData && typeof routesData === 'object' && !Array.isArray(routesData)) {
        routesData = routesData.routes || routesData.data || [];
      }
      
      if (sitesData && typeof sitesData === 'object' && !Array.isArray(sitesData)) {
        sitesData = sitesData.sites || sitesData.data || [];
      }
      
      // Ensure we always have arrays
      setRoutes(Array.isArray(routesData) ? routesData : []);
      setSites(Array.isArray(sitesData) ? sitesData : []);
      
    } catch (error) {
      console.error('[Routes] Fetch error:', error);
      console.error('[Routes] Error response:', error.response?.data);
      
      // Set error state for UI banner
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to load routes';
      
      setFetchError(errorMessage);
      
      // Ensure routes/sites default to empty arrays on error
      setRoutes([]);
      setSites([]);
      
      // Don't show toast if it's an auth error (will redirect)
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setRouteName('');
    setRouteActive(true);
    setStops([]);
    setEditingRoute(null);
  };

  const resetStopForm = () => {
    setStopType('location');
    setStopSiteId('');
    setStopLabel('');
    setStopAddress('');
    setStopLat('');
    setStopLng('');
    setStopGeofence(75);
    setStopRequired(false); // Default OFF - GPS check-in is optional for route creation
    setEditingStop(null);
    setStopFormError('');
    setAddressQuery('');
    setAddressSuggestions([]);
    setAddressValidated(false);
    setAddressEntryMode('autocomplete');
    // Patrols v2: Reset sector selection
    setSelectedLocationId('');
    setLocationSectors([]);
    setSelectedSectorId('');
  };

  // Patrols v2: Fetch locations for a site
  const fetchLocationsForSite = async (siteId) => {
    if (!siteId) {
      setSectorLocations([]);
      return;
    }
    
    try {
      setLoadingLocations(true);
      const res = await api.get(`/sites/${siteId}/locations?include_sectors=false`);
      setSectorLocations(res.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSectorLocations([]);
      toast.error('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  // Patrols v2: Fetch sectors for a location
  const fetchSectorsForLocation = async (locationId) => {
    if (!locationId) {
      setLocationSectors([]);
      return;
    }
    
    try {
      setLoadingSectors(true);
      const res = await api.get(`/locations/${locationId}/sectors`);
      setLocationSectors(res.data || []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
      setLocationSectors([]);
      toast.error('Failed to load sectors');
    } finally {
      setLoadingSectors(false);
    }
  };

  // Patrols v2: Handle sector selection
  const handleSectorSelect = (sectorId) => {
    const sector = locationSectors.find(s => s.id === sectorId);
    if (sector) {
      setSelectedSectorId(sectorId);
      setStopLabel(sector.name);
      setStopAddress(sector.description || `Sector: ${sector.name}`);
      if (sector.gps_lat && sector.gps_lng) {
        setStopLat(sector.gps_lat.toString());
        setStopLng(sector.gps_lng.toString());
        setAddressValidated(true);
      }
      setStopGeofence(sector.geofence_radius_m || 75);
    }
  };

  // Address search function
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 5) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setIsSearchingAddress(true);
    try {
      const response = await api.get(`/address/search?q=${encodeURIComponent(query)}&limit=5`);
      setAddressSuggestions(response.data || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
      setAddressSuggestions([]);
    } finally {
      setIsSearchingAddress(false);
    }
  }, [api]);

  // Debounced address search
  const handleAddressInputChange = (value) => {
    setAddressQuery(value);
    setAddressValidated(false);
    setStopFormError('');
    
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    
    addressDebounceRef.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  // Validate address has street number
  const validateAddress = (suggestion) => {
    const streetNumber = suggestion.street_number;
    const road = suggestion.street_name;
    const suburb = suggestion.suburb || suggestion.city;
    const state = suggestion.state;

    if (!streetNumber || !road || !suburb || !state) {
      return {
        valid: false,
        error: 'Please select a full street address including street number (e.g., "52 Hastings Drive Raymond Terrace NSW")'
      };
    }
    return { valid: true, error: null };
  };

  // Handle address selection from dropdown
  const handleAddressSelect = (suggestion) => {
    const validation = validateAddress(suggestion);
    
    if (!validation.valid) {
      setStopFormError(validation.error);
      setAddressValidated(false);
      return;
    }

    // Build formatted address
    const formattedAddress = suggestion.display_name;
    
    setStopAddress(formattedAddress);
    setAddressQuery(formattedAddress);
    setStopLat(suggestion.lat?.toString() || '');
    setStopLng(suggestion.lng?.toString() || '');
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setAddressValidated(true);
    setStopFormError('');
    
    // Auto-fill label if empty
    if (!stopLabel) {
      const shortAddress = [
        suggestion.street_number,
        suggestion.street_name,
        suggestion.suburb
      ].filter(Boolean).join(' ');
      setStopLabel(shortAddress || `Stop ${stops.length + 1}`);
    }
  };

  // Use current GPS location
  const handleUseCurrentLocation = async () => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation not supported');
      return;
    }

    setIsSearchingAddress(true);
    setStopFormError('');
    setAddressEntryMode('gps'); // P0 FIX: Switch to GPS mode

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
        });
      });

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;

      // Set coordinates immediately - this is the key fix
      setStopLat(lat.toFixed(6));
      setStopLng(lng.toFixed(6));

      // Reverse geocode (optional - for address display)
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
          { headers: { 'User-Agent': 'BSMS-Security-Platform/1.0' } }
        );
        const data = await response.json();

        if (data && data.address) {
          const addr = data.address;
          const streetNumber = addr.house_number;
          const road = addr.road;
          const suburb = addr.suburb || addr.neighbourhood || addr.town;

          const formattedAddress = data.display_name;
          setStopAddress(formattedAddress);
          setAddressQuery(formattedAddress);
          
          // P0 FIX: Set validated even without street number - GPS provides coordinates
          setAddressValidated(true);
          
          // Auto-set label from address if not set
          if (!stopLabel) {
            const labelParts = [streetNumber, road, suburb].filter(Boolean);
            if (labelParts.length > 0) {
              setStopLabel(labelParts.join(' '));
            } else {
              setStopLabel(`GPS Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
            }
          }
          
          toast.success('GPS location captured');
        } else {
          // Still save with GPS coordinates even without address
          setStopAddress(`GPS: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          if (!stopLabel) {
            setStopLabel(`GPS Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
          }
          setAddressValidated(true);
          toast.success('GPS coordinates captured (address unavailable)');
        }
      } catch (geocodeError) {
        // Geocode failed but we have coordinates - that's OK
        console.warn('Reverse geocode failed:', geocodeError);
        setStopAddress(`GPS: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
        if (!stopLabel) {
          setStopLabel(`GPS Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`);
        }
        setAddressValidated(true);
        toast.success('GPS coordinates captured');
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      if (error.code === 1) {
        toast.error('Location permission denied');
      } else {
        toast.error('Failed to get current location');
      }
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleMapClick = (latlng) => {
    if (showCreateDialog || showEditDialog) {
      // P0 FIX: Set coordinates immediately, then try to get address
      setIsSearchingAddress(true);
      setStopFormError('');
      setAddressEntryMode('map_pin'); // Switch to map pin mode
      
      // Always set coordinates first
      setStopLat(latlng.lat.toFixed(6));
      setStopLng(latlng.lng.toFixed(6));
      
      fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1`,
        { headers: { 'User-Agent': 'BSMS-Security-Platform/1.0' } }
      )
        .then(res => res.json())
        .then(data => {
          if (data && data.address) {
            const addr = data.address;
            const streetNumber = addr.house_number;
            const road = addr.road;
            const suburb = addr.suburb || addr.neighbourhood || addr.town;

            const formattedAddress = data.display_name;
            setStopAddress(formattedAddress);
            setAddressQuery(formattedAddress);
            // P0 FIX: Always validate when we have coordinates from map click
            setAddressValidated(true);
            
            // Auto-set label
            const labelParts = [streetNumber, road, suburb].filter(Boolean);
            if (labelParts.length > 0) {
              setStopLabel(labelParts.join(' '));
            } else {
              setStopLabel(`Map Pin (${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)})`);
            }
            
            toast.success('Location pinned on map');
          } else {
            // No address found, but coordinates are valid
            setStopAddress(`Map Pin: ${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
            setStopLabel(`Map Pin (${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)})`);
            setAddressValidated(true);
            toast.success('Location pinned (address unavailable)');
          }
          setShowStopDialog(true);
          setStopType('location');
        })
        .catch(() => {
          // Geocode failed but coordinates are valid
          setStopAddress(`Map Pin: ${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
          setStopLabel(`Map Pin (${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)})`);
          setAddressValidated(true);
          setShowStopDialog(true);
          setStopType('location');
          toast.success('Location pinned');
        })
        .finally(() => setIsSearchingAddress(false));
    }
  };

  const handleSiteSelect = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (site) {
      setStopSiteId(siteId);
      setStopLabel(site.name);
      setStopLat(site.latitude?.toString() || '');
      setStopLng(site.longitude?.toString() || '');
      setStopAddress(site.full_address || site.formatted_address || site.address || '');
      setAddressQuery(site.full_address || site.formatted_address || site.address || '');
      setAddressValidated(true); // Sites are pre-validated
      setStopFormError('');
    }
  };

  const handleAddStop = () => {
    setStopFormError('');
    
    // Validate required fields
    if (!stopLabel.trim()) {
      setStopFormError('Please enter a label for this stop');
      return;
    }

    if (stopType === 'location') {
      // P0 FIX: Coordinates are ALWAYS required for location-type stops
      // Backend validation requires lat/lng, so frontend must too
      
      if (addressEntryMode === 'autocomplete') {
        // Autocomplete mode - need address selection with coordinates
        if (!addressValidated || !stopAddress) {
          setStopFormError('Please select a valid street address from the dropdown, or switch to Manual mode.');
          return;
        }
      } else if (addressEntryMode === 'manual') {
        // Manual mode - need address text
        if (!stopAddress.trim()) {
          setStopFormError('Please enter an address');
          return;
        }
      }
      // Map Pin / GPS modes - coordinates will be set by those methods
      
      // P0 FIX: ALWAYS require coordinates for location stops (backend requires them)
      if (!stopLat || !stopLng) {
        setStopFormError('Coordinates are required. Use GPS button, Map Pin, or select an address from Search mode.');
        return;
      }
    } else if (stopType === 'site') {
      // For site type, require site selection
      if (!stopSiteId) {
        setStopFormError('Please select a site');
        return;
      }
    } else if (stopType === 'sector') {
      // Patrols v2: For sector type, require sector selection
      if (!selectedSectorId) {
        setStopFormError('Please select a sector');
        return;
      }
      // Sectors with Required Check-in need GPS
      if (stopRequired && (!stopLat || !stopLng)) {
        setStopFormError('Required Check-in is enabled but sector has no GPS coordinates.');
        return;
      }
    }

    // P0 FIX: Generate stable unique ID for new stops
    const generateStopId = () => {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return `stop-${crypto.randomUUID()}`;
      }
      return `stop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    const newStop = {
      id: editingStop ? editingStop.id : generateStopId(),
      type: stopType,
      site_id: stopType === 'site' ? stopSiteId : (stopType === 'sector' ? stopSiteId : null),
      sector_id: stopType === 'sector' ? selectedSectorId : null,
      location_id: stopType === 'sector' ? selectedLocationId : null,
      label: stopLabel.trim(),
      full_address: stopAddress || null,
      lat: stopLat ? parseFloat(stopLat) : null,
      lng: stopLng ? parseFloat(stopLng) : null,
      geofence_m: stopGeofence,
      required_checkin: stopRequired,
      order: editingStop ? editingStop.order : (Array.isArray(stops) ? stops.length : 0)
    };

    if (editingStop) {
      setStops(prevStops => {
        if (!Array.isArray(prevStops)) return [newStop];
        return prevStops.map(s => s?.id === editingStop.id ? newStop : s);
      });
    } else {
      setStops(prevStops => {
        if (!Array.isArray(prevStops)) return [newStop];
        return [...prevStops, newStop];
      });
    }

    setShowStopDialog(false);
    resetStopForm();
    toast.success(editingStop ? 'Stop updated' : 'Stop added');
  };

  const handleRemoveStop = (stopId) => {
    // P0 FIX: Guard against null/undefined stopId
    if (!stopId) {
      console.error('[handleRemoveStop] stopId is null/undefined');
      return;
    }
    setStops(prevStops => {
      if (!Array.isArray(prevStops)) return [];
      return prevStops.filter(s => s?.id !== stopId);
    });
  };

  const handleEditStop = (stop) => {
    // P0 FIX: Guard against null/undefined stop
    if (!stop) {
      console.error('[handleEditStop] Stop is null/undefined');
      toast.error('Cannot edit stop - invalid data');
      return;
    }
    
    setEditingStop(stop);
    setStopType(stop.type || 'location');
    setStopSiteId(stop.site_id || '');
    setStopLabel(stop.label || '');
    setStopAddress(stop.full_address || '');
    setAddressQuery(stop.full_address || '');
    // P0 FIX: Guard against null lat/lng - use empty string instead of calling toString() on null
    setStopLat(stop.lat != null ? String(stop.lat) : '');
    setStopLng(stop.lng != null ? String(stop.lng) : '');
    setStopGeofence(stop.geofence_m || 75);
    setStopRequired(stop.required_checkin || false);
    setAddressValidated(!!stop.full_address || stop.type === 'site');
    setStopFormError('');
    setShowStopDialog(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    // P0 FIX: Guard against null/undefined active or over
    if (!active?.id || !over?.id || active.id === over.id) {
      return;
    }
    setStops((items) => {
      // P0 FIX: Guard against items being null/undefined
      if (!Array.isArray(items)) {
        console.error('[handleDragEnd] Items is not an array:', items);
        return [];
      }
      const oldIndex = items.findIndex(i => i?.id === active.id);
      const newIndex = items.findIndex(i => i?.id === over.id);
      if (oldIndex === -1 || newIndex === -1) {
        console.error('[handleDragEnd] Could not find indices:', { oldIndex, newIndex, activeId: active.id, overId: over.id });
        return items;
      }
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleCreateRoute = async () => {
    // Prevent double-click
    if (isSubmitting) {
      return;
    }
    
    // P0 FIX: Defensive null check for stops
    const safeStops = Array.isArray(stops) ? stops : [];
    
    // Client-side validation
    if (!routeName.trim()) {
      toast.error('Please enter a route name');
      return;
    }
    if (safeStops.length < 2) {
      toast.error('Route must have at least 2 stops');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        name: routeName.trim(),
        active: routeActive,
        stops: safeStops.map((s, i) => ({
          type: s?.type,
          site_id: s?.site_id,
          label: s?.label,
          full_address: s?.full_address || null,
          lat: s?.lat,
          lng: s?.lng,
          geofence_m: s?.geofence_m,
          required_checkin: s?.required_checkin,
          order: i
        }))
      };

      // Debug log for development
      console.log('[CreateRoute] Payload:', JSON.stringify(payload, null, 2));
      
      const response = await api.post('/routes', payload);
      
      // Debug log response
      console.log('[CreateRoute] Response:', JSON.stringify(response.data, null, 2));
      
      // Handle structured response
      if (response.data?.success === false) {
        toast.error(response.data.message || 'Failed to create route');
        return;
      }
      
      // Success - close dialog, reset form, and show success message
      toast.success('Route created successfully');
      setShowCreateDialog(false);
      resetForm();
      
      // Refresh routes list
      await fetchData();
      
    } catch (error) {
      // Debug log error
      console.error('[CreateRoute] Error:', error);
      console.error('[CreateRoute] Error response:', error.response?.data);
      
      // Extract error message from various response formats
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to create route. Please try again.';
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRoute = async () => {
    if (!routeName.trim()) {
      toast.error('Please enter a route name');
      return;
    }

    // P0 FIX: Defensive null check for stops
    const safeStops = Array.isArray(stops) ? stops : [];

    setIsSubmitting(true);
    try {
      const payload = {
        name: routeName.trim(),
        active: routeActive,
        stops: safeStops.map((s, i) => ({
          type: s?.type,
          site_id: s?.site_id,
          label: s?.label,
          full_address: s?.full_address || null,
          lat: s?.lat,
          lng: s?.lng,
          geofence_m: s?.geofence_m,
          required_checkin: s?.required_checkin,
          order: i
        }))
      };

      await api.put(`/routes/${editingRoute.id}`, payload);
      toast.success('Route updated successfully');
      setShowEditDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating route:', error);
      toast.error(error.response?.data?.detail || 'Failed to update route');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchiveRoute = async (routeId) => {
    if (!window.confirm(
      'Archive this route? It will be hidden from scheduling, but historical patrol records will remain intact.'
    )) return;

    try {
      await api.delete(`/routes/${routeId}`);
      toast.success('Route archived');
      fetchData();
    } catch (error) {
      console.error('Error archiving route:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive route');
    }
  };

  const openEditDialog = (route) => {
    if (!route) return;
    
    setEditingRoute(route);
    setRouteName(route.name || '');
    setRouteActive(route.active ?? true);
    
    // P0 FIX: Defensive - ensure stops is an array and each stop has valid ID
    const routeStops = Array.isArray(route.stops) ? route.stops : [];
    setStops(routeStops.map((s, i) => {
      // Ensure each stop has all required fields with defaults
      return {
        id: s?.id || `stop-${Date.now()}-${i}`,
        type: s?.type || 'location',
        site_id: s?.site_id || null,
        sector_id: s?.sector_id || null,
        location_id: s?.location_id || null,
        label: s?.label || `Stop ${i + 1}`,
        full_address: s?.full_address || null,
        lat: s?.lat != null ? s.lat : null,
        lng: s?.lng != null ? s.lng : null,
        geofence_m: s?.geofence_m || 75,
        required_checkin: s?.required_checkin ?? false,
        order: s?.order ?? i
      };
    }));
    
    setShowEditDialog(true);
  };

  const openViewDialog = (route) => {
    if (!route) return;
    setViewingRoute(route);
    setShowViewDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  // P1 FIX: Only use stops with valid coordinates for map center
  const stopsWithCoords = stops.filter(s => s.lat != null && s.lng != null);
  const mapCenter = stopsWithCoords.length > 0 
    ? [stopsWithCoords[0].lat, stopsWithCoords[0].lng] 
    : defaultCenter;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white">ROUTE BUILDER</h1>
          <p className="text-zinc-500 text-sm">{routes.length} routes configured</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-500 hover:bg-blue-600"
            data-testid="create-route-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Route
          </Button>
        )}
      </div>

      {/* Error Banner */}
      {fetchError && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <div className="flex-1">
              <p className="text-sm text-red-400">{fetchError}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Routes List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.isArray(routes) && routes.map(route => {
          // Defensive: ensure route and route.stops exist
          const routeStops = Array.isArray(route?.stops) ? route.stops : [];
          const stopsCount = routeStops.length;
          
          return (
            <Card key={route?.id || Math.random()} className="bg-[#18181b] border-[#3f3f46]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-rajdhani text-white flex items-center gap-2">
                    <Route className="w-5 h-5 text-blue-400" />
                    {route?.name || 'Unnamed Route'}
                  </CardTitle>
                  <Badge className={`text-xs ${route?.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                    {route?.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{stopsCount} stops</span>
                  </div>
                  
                  {/* Stop preview */}
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {routeStops.slice(0, 3).map((stop, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                        <span className="w-4 h-4 rounded-full bg-blue-500/30 text-blue-400 flex items-center justify-center text-[10px]">
                          {i + 1}
                        </span>
                        <span className="truncate">{stop?.label || 'Unnamed Stop'}</span>
                      </div>
                    ))}
                    {stopsCount > 3 && (
                      <div className="text-xs text-zinc-600">+{stopsCount - 3} more</div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-[#3f3f46]">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openViewDialog(route)}
                      className="flex-1 border-zinc-600"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    {isAdmin && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditDialog(route)}
                          className="flex-1 border-zinc-600"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleArchiveRoute(route?.id)}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                            title="Archive Route"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Archive
                          </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {(!Array.isArray(routes) || routes.length === 0) && !fetchError && (
          <Card className="bg-[#18181b] border-[#3f3f46] col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Route className="w-12 h-12 text-zinc-600 mb-4" />
              <p className="text-zinc-500">No routes configured yet</p>
              {isAdmin && (
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="mt-4 bg-blue-500 hover:bg-blue-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Route
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Route Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
          resetForm();
        }
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-400" />
              {editingRoute ? 'EDIT ROUTE' : 'CREATE NEW ROUTE'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Click on the map to add stops, then drag to reorder.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4 h-[60vh]">
            {/* Left Panel - Route Details & Stops */}
            <div className="space-y-4 overflow-y-auto pr-2">
              <div className="space-y-2">
                <Label className="text-zinc-300">Route Name</Label>
                <Input
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  placeholder="e.g., Sydney CBD Patrol"
                  className="bg-[#09090b] border-[#3f3f46]"
                  data-testid="route-name-input"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Active</Label>
                <Switch 
                  checked={routeActive} 
                  onCheckedChange={setRouteActive}
                />
              </div>

              <div className="border-t border-[#3f3f46] pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-zinc-300">Stops ({stops.length})</Label>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      resetStopForm();
                      setShowStopDialog(true);
                    }}
                    className="border-zinc-600 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Stop
                  </Button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={(stops || []).map(s => s?.id || `stop-${Math.random()}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {(stops || []).map((stop, index) => (
                      <SortableStop
                        key={stop.id}
                        stop={stop}
                        index={index}
                        onRemove={handleRemoveStop}
                        onEdit={handleEditStop}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {(!stops || stops.length === 0) && (
                  <p className="text-xs text-zinc-500 text-center py-4">
                    Click on the map or use "Add Stop" to add stops
                  </p>
                )}
              </div>
            </div>

            {/* Right Panel - Map */}
            <div className="col-span-2 rounded-lg overflow-hidden border border-[#3f3f46]">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onMapClick={handleMapClick} />
                {stops && stops.length > 0 && <MapBoundsAdjuster stops={stops} />}
                
                {/* Markers - P0 FIX: Only render stops with valid finite coordinates */}
                {(stops || []).filter(stop => 
                  stop && 
                  Number.isFinite(parseFloat(stop.lat)) && 
                  Number.isFinite(parseFloat(stop.lng))
                ).map((stop, index) => (
                  <React.Fragment key={stop.id}>
                    <Marker
                      position={[stop.lat, stop.lng]}
                      icon={createMarkerIcon('#3b82f6', index + 1)}
                    >
                      <Popup>
                        <div className="text-sm">
                          <strong>{stop.label}</strong>
                          <br />
                          <span className="text-gray-600">
                            {stop.type === 'site' ? 'Site Stop' : 'Address Stop'}
                          </span>
                          <br />
                          <span className="text-gray-600">
                            Geofence: {stop.geofence_m}m
                          </span>
                          {stop.full_address && (
                            <>
                              <br />
                              <span className="text-gray-500 text-xs">{stop.full_address}</span>
                            </>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                    {/* Geofence circle - only render if required_checkin is enabled */}
                    {stop.required_checkin && (
                      <Circle
                        center={[stop.lat, stop.lng]}
                        radius={stop.geofence_m}
                        pathOptions={{ 
                          color: '#3b82f6', 
                          fillColor: '#3b82f6', 
                          fillOpacity: 0.1,
                          weight: 1,
                          dashArray: '5, 5'
                        }}
                      />
                    )}
                  </React.Fragment>
                ))}

                {/* Route line - P0 FIX: Only include stops with valid finite coordinates */}
                {(() => {
                  const safeStops = stops || [];
                  const stopsWithCoords = safeStops.filter(s => 
                    s && 
                    Number.isFinite(parseFloat(s.lat)) && 
                    Number.isFinite(parseFloat(s.lng))
                  );
                  return stopsWithCoords.length > 1 && (
                    <Polyline
                      positions={stopsWithCoords.map(s => [parseFloat(s.lat), parseFloat(s.lng)])}
                      color="#3b82f6"
                      weight={3}
                      opacity={0.7}
                      dashArray="10, 10"
                    />
                  );
                })()}
              </MapContainer>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
                resetForm();
              }}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button
              onClick={editingRoute ? handleUpdateRoute : handleCreateRoute}
              disabled={isSubmitting}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              {editingRoute ? 'Update Route' : 'Create Route'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Stop Dialog */}
      <Dialog open={showStopDialog} onOpenChange={(open) => {
        if (!open) {
          setShowStopDialog(false);
          resetStopForm();
        }
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              {editingStop ? 'EDIT STOP' : 'ADD STOP'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Error message */}
            {stopFormError && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{stopFormError}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-zinc-300">Stop Type</Label>
              <Select value={stopType} onValueChange={(value) => {
                setStopType(value);
                setStopFormError('');
                if (value === 'location') {
                  setStopSiteId('');
                }
                // Patrols v2: Reset sector selection when changing type
                setSelectedLocationId('');
                setLocationSectors([]);
                setSelectedSectorId('');
              }}>
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="stop-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  <SelectItem value="location">Custom Address</SelectItem>
                  <SelectItem value="site">Existing Site</SelectItem>
                  <SelectItem value="sector">Patrol Sector</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Patrols v2: Sector Selection UI */}
            {stopType === 'sector' && (
              <div className="space-y-4">
                {/* Site selector */}
                <div className="space-y-2">
                  <Label className="text-zinc-300">Select Site</Label>
                  <Select 
                    value={stopSiteId} 
                    onValueChange={(siteId) => {
                      setStopSiteId(siteId);
                      setSelectedLocationId('');
                      setLocationSectors([]);
                      setSelectedSectorId('');
                      setStopLabel('');
                      setStopLat('');
                      setStopLng('');
                      fetchLocationsForSite(siteId);
                    }}
                  >
                    <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                      <SelectValue placeholder="Choose a site..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location selector */}
                {stopSiteId && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Select Location</Label>
                    {loadingLocations ? (
                      <div className="flex items-center justify-center p-4 bg-[#09090b] rounded border border-[#3f3f46]">
                        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                      </div>
                    ) : sectorLocations.length === 0 ? (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                        No locations defined for this site. Go to Patrols → Locations & Sectors to create some.
                      </div>
                    ) : (
                      <Select 
                        value={selectedLocationId} 
                        onValueChange={(locId) => {
                          setSelectedLocationId(locId);
                          setSelectedSectorId('');
                          setStopLabel('');
                          setStopLat('');
                          setStopLng('');
                          fetchSectorsForLocation(locId);
                        }}
                      >
                        <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                          <SelectValue placeholder="Choose a location..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                          {sectorLocations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>
                              {loc.name} {loc.sector_count > 0 && `(${loc.sector_count} sectors)`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Sector selector */}
                {selectedLocationId && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Select Sector</Label>
                    {loadingSectors ? (
                      <div className="flex items-center justify-center p-4 bg-[#09090b] rounded border border-[#3f3f46]">
                        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                      </div>
                    ) : locationSectors.length === 0 ? (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                        No sectors defined for this location. Go to Patrols → Locations & Sectors to create some.
                      </div>
                    ) : (
                      <Select 
                        value={selectedSectorId} 
                        onValueChange={handleSectorSelect}
                        data-testid="sector-select"
                      >
                        <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                          <SelectValue placeholder="Choose a sector..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                          {locationSectors.map(sector => (
                            <SelectItem key={sector.id} value={sector.id}>
                              {sector.visit_order}. {sector.name} {sector.gps_lat && '(GPS)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                {/* Selected sector info */}
                {selectedSectorId && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded">
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Sector: {stopLabel}</span>
                    </div>
                    {stopLat && stopLng && (
                      <div className="text-xs text-zinc-500 mt-1">
                        GPS: {parseFloat(stopLat).toFixed(6)}, {parseFloat(stopLng).toFixed(6)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {stopType === 'site' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Select Site</Label>
                <Select value={stopSiteId} onValueChange={handleSiteSelect}>
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]" data-testid="site-select">
                    <SelectValue placeholder="Choose a site..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {stopType === 'location' && (
              <>
                {/* P0 FIX: Address Entry Mode Selector */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-xs uppercase tracking-wider">Entry Method</Label>
                  <div className="grid grid-cols-4 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setAddressEntryMode('autocomplete');
                        setStopFormError('');
                      }}
                      className={`p-2 rounded text-xs flex flex-col items-center gap-1 transition-colors ${
                        addressEntryMode === 'autocomplete' 
                          ? 'bg-blue-500/20 border border-blue-500 text-blue-400' 
                          : 'bg-[#09090b] border border-[#3f3f46] text-zinc-400 hover:bg-[#27272a]'
                      }`}
                      data-testid="mode-autocomplete"
                    >
                      <Search className="w-4 h-4" />
                      Search
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAddressEntryMode('manual');
                        setStopFormError('');
                      }}
                      className={`p-2 rounded text-xs flex flex-col items-center gap-1 transition-colors ${
                        addressEntryMode === 'manual' 
                          ? 'bg-blue-500/20 border border-blue-500 text-blue-400' 
                          : 'bg-[#09090b] border border-[#3f3f46] text-zinc-400 hover:bg-[#27272a]'
                      }`}
                      data-testid="mode-manual"
                    >
                      <Edit className="w-4 h-4" />
                      Manual
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAddressEntryMode('map_pin');
                        setStopFormError('');
                        toast.info('Click on the map to set location');
                      }}
                      className={`p-2 rounded text-xs flex flex-col items-center gap-1 transition-colors ${
                        addressEntryMode === 'map_pin' 
                          ? 'bg-blue-500/20 border border-blue-500 text-blue-400' 
                          : 'bg-[#09090b] border border-[#3f3f46] text-zinc-400 hover:bg-[#27272a]'
                      }`}
                      data-testid="mode-map-pin"
                    >
                      <MapPin className="w-4 h-4" />
                      Map Pin
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleUseCurrentLocation();
                      }}
                      disabled={isSearchingAddress}
                      className={`p-2 rounded text-xs flex flex-col items-center gap-1 transition-colors ${
                        addressEntryMode === 'gps' 
                          ? 'bg-emerald-500/20 border border-emerald-500 text-emerald-400' 
                          : 'bg-[#09090b] border border-[#3f3f46] text-zinc-400 hover:bg-[#27272a]'
                      }`}
                      data-testid="mode-gps"
                    >
                      <Crosshair className="w-4 h-4" />
                      GPS
                    </button>
                  </div>
                </div>

                {/* Address Input - Autocomplete Mode */}
                {addressEntryMode === 'autocomplete' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Search Address</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        ref={addressInputRef}
                        value={addressQuery}
                        onChange={(e) => handleAddressInputChange(e.target.value)}
                        onFocus={() => addressQuery.length >= 5 && addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                        placeholder="e.g., 36 Honeysuckle Drive Newcastle"
                        className="pl-10 pr-8 bg-[#09090b] border-[#3f3f46]"
                        data-testid="address-input"
                      />
                      {isSearchingAddress && (
                        <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400 animate-spin" />
                      )}
                      {!isSearchingAddress && addressValidated && (
                        <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-emerald-400" />
                      )}
                    </div>
                    
                    {/* Address suggestions dropdown */}
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[#18181b] border border-[#3f3f46] rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {addressSuggestions.map((suggestion, idx) => {
                          const hasStreetNumber = !!suggestion.street_number;
                          return (
                            <button
                              key={suggestion.place_id || idx}
                              type="button"
                              onClick={() => handleAddressSelect(suggestion)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-700/50 transition-colors flex items-start gap-2 ${
                                !hasStreetNumber ? 'opacity-50' : ''
                              }`}
                              data-testid={`address-suggestion-${idx}`}
                            >
                              <MapPin className="w-4 h-4 mt-0.5 text-zinc-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className={`font-medium truncate ${hasStreetNumber ? 'text-zinc-200' : 'text-zinc-400'}`}>
                                  {[suggestion.street_number, suggestion.street_name, suggestion.suburb].filter(Boolean).join(' ') || suggestion.display_name.split(',')[0]}
                                </div>
                                <div className="text-xs text-zinc-500 truncate">
                                  {suggestion.state} {suggestion.postcode}
                                </div>
                                {!hasStreetNumber && (
                                  <div className="text-xs text-amber-400 mt-0.5">
                                    Missing street number
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    {addressQuery.length > 0 && addressQuery.length < 5 && !showAddressSuggestions && (
                      <p className="text-xs text-zinc-500">Type at least 5 characters to search</p>
                    )}
                    
                    {/* Tip to switch modes */}
                    {!addressValidated && addressQuery.length >= 5 && addressSuggestions.length === 0 && !isSearchingAddress && (
                      <p className="text-xs text-amber-400">
                        No results? Try Manual mode or click on the map
                      </p>
                    )}
                  </div>
                )}

                {/* Address Input - Manual Mode */}
                {addressEntryMode === 'manual' && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Address (Manual Entry)</Label>
                    <Input
                      value={stopAddress}
                      onChange={(e) => setStopAddress(e.target.value)}
                      placeholder="e.g., 36 Honeysuckle Drive, Newcastle NSW 2300"
                      className="bg-[#09090b] border-[#3f3f46]"
                      data-testid="manual-address-input"
                    />
                    {/* P0 FIX: Always show GPS/Map Pin options for Manual mode since coordinates are always required */}
                    {(!stopLat || !stopLng) ? (
                      <>
                        <p className="text-xs text-amber-400">
                          <AlertCircle className="w-3 h-3 inline mr-1" />
                          Coordinates required. Use GPS or click on the map to set location.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleUseCurrentLocation}
                            disabled={isSearchingAddress}
                            className="flex-1 border-zinc-600 text-xs"
                          >
                            <Crosshair className="w-3 h-3 mr-1" />
                            Use GPS
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setAddressEntryMode('map_pin');
                              toast.info('Click on the map to set coordinates');
                            }}
                            className="flex-1 border-zinc-600 text-xs"
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            Click Map
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-emerald-400">
                        <CheckCircle2 className="w-3 h-3 inline mr-1" />
                        Coordinates set: {parseFloat(stopLat).toFixed(4)}, {parseFloat(stopLng).toFixed(4)}
                      </p>
                    )}
                  </div>
                )}

                {/* Map Pin / GPS Mode - Show current state */}
                {(addressEntryMode === 'map_pin' || addressEntryMode === 'gps') && (
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Address</Label>
                    {stopAddress ? (
                      <div className="p-2 bg-[#09090b] rounded-md border border-[#3f3f46]">
                        <p className="text-sm text-zinc-300">{stopAddress}</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                        {addressEntryMode === 'map_pin' 
                          ? 'Click on the map to set location' 
                          : 'Click GPS button to capture your location'}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* P0 FIX: Label is always required and separate from Address */}
            <div className="space-y-2">
              <Label className="text-zinc-300">
                Label <span className="text-red-400">*</span>
                <span className="text-zinc-500 text-xs ml-2">(Human-readable name)</span>
              </Label>
              <Input
                value={stopLabel}
                onChange={(e) => setStopLabel(e.target.value)}
                placeholder="e.g., Head Office, Main Entry, Car Park A"
                className="bg-[#09090b] border-[#3f3f46]"
                data-testid="stop-label-input"
              />
              <p className="text-xs text-zinc-500">This name will appear on patrol routes and reports</p>
            </div>

            {/* P0 FIX: Only show coordinates info when they're set */}
            {(stopLat && stopLng) && (
              <div className="p-2 bg-emerald-500/10 rounded-md border border-emerald-500/30">
                <div className="flex items-center gap-2 text-xs text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>Coordinates Set: {parseFloat(stopLat).toFixed(6)}, {parseFloat(stopLng).toFixed(6)}</span>
                </div>
              </div>
            )}
            
            {/* P0 FIX: Warning when coordinates missing for location stops */}
            {stopType === 'location' && (!stopLat || !stopLng) && (
              <div className="p-2 bg-amber-500/10 rounded-md border border-amber-500/30">
                <div className="flex items-center gap-2 text-xs text-amber-400">
                  <AlertCircle className="w-3 h-3" />
                  <span>Coordinates required. Use GPS, Map Pin, or select address from Search.</span>
                </div>
              </div>
            )}

            {/* P1 FIX: Hide geofence unless Required Check-in is ON */}
            {stopRequired && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Geofence Radius (meters)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={stopGeofence}
                    onChange={(e) => setStopGeofence(Math.max(50, Math.min(150, parseInt(e.target.value) || 75)))}
                    min={50}
                    max={150}
                    className="bg-[#09090b] border-[#3f3f46] w-24"
                    data-testid="geofence-input"
                  />
                  <input
                    type="range"
                    value={stopGeofence}
                    onChange={(e) => setStopGeofence(parseInt(e.target.value))}
                    min={50}
                    max={150}
                    className="flex-1 accent-blue-500"
                  />
                </div>
                <p className="text-xs text-zinc-500">Check-in radius: {stopGeofence}m</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label className="text-zinc-300">Required Check-in</Label>
              <Switch 
                checked={stopRequired} 
                onCheckedChange={setStopRequired}
                data-testid="required-checkin-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowStopDialog(false);
                resetStopForm();
              }}
              className="border-zinc-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddStop} 
              className="bg-blue-500 hover:bg-blue-600"
              disabled={isSearchingAddress}
              data-testid="save-stop-btn"
            >
              {isSearchingAddress ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {editingStop ? 'Update Stop' : 'Add Stop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Route Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <Route className="w-5 h-5 text-blue-400" />
              {viewingRoute?.name || 'View Route'}
            </DialogTitle>
          </DialogHeader>

          {viewingRoute && (() => {
            // Defensive: ensure stops is always an array
            const viewStops = Array.isArray(viewingRoute.stops) ? viewingRoute.stops : [];
            
            return (
              <div className="grid grid-cols-2 gap-4">
                {/* Stops List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  <Label className="text-zinc-400 text-sm">STOPS ({viewStops.length})</Label>
                  {viewStops.length === 0 ? (
                    <p className="text-zinc-500 text-sm py-4">No stops configured</p>
                  ) : (
                    viewStops.map((stop, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-[#27272a] rounded-md">
                        <div className="w-8 h-8 rounded-full bg-blue-500/30 text-blue-400 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-zinc-200">{stop?.label || 'Unnamed Stop'}</p>
                          <p className="text-xs text-zinc-500">
                            {stop?.type === 'site' ? 'Site' : 'Location'} • {stop?.geofence_m || 75}m radius
                            {stop?.required_checkin && ' • Required'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

              {/* Map Preview */}
              <div className="h-96 rounded-lg overflow-hidden border border-[#3f3f46]">
                <MapContainer
                  center={viewStops.length > 0 && viewStops[0]?.lat && viewStops[0]?.lng
                    ? [viewStops[0].lat, viewStops[0].lng]
                    : defaultCenter
                  }
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {viewStops.length > 0 && viewStops[0]?.lat && (
                    <MapBoundsAdjuster stops={viewStops.filter(s => s?.lat && s?.lng)} />
                  )}
                  
                  {viewStops.filter(s => s?.lat && s?.lng).map((stop, index) => (
                    <Marker
                      key={index}
                      position={[stop.lat, stop.lng]}
                      icon={createMarkerIcon('#3b82f6', index + 1)}
                    >
                      <Popup>{stop?.label || 'Stop'}</Popup>
                    </Marker>
                  ))}

                  {viewStops.length > 1 && (
                    <Polyline
                      positions={viewStops.filter(s => s?.lat && s?.lng).map(s => [s.lat, s.lng])}
                      color="#3b82f6"
                      weight={3}
                      opacity={0.7}
                      dashArray="10, 10"
                    />
                  )}
                </MapContainer>
              </div>
            </div>
            );
          })()}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowViewDialog(false)}
              className="border-zinc-600"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
